/**
 * What each role on a creator account is allowed to do.
 *
 * Roles are coarse because the people using them are: a creator, their manager,
 * and someone from an agency who should only be able to look. Everything the UI
 * hides is decided by the same table the server enforces, so a hidden button and
 * a rejected request always agree.
 */

/** Stored roles plus the derived one. "owner" is the creator themselves. */
export const MEMBER_ROLES = ["owner", "admin", "manager", "viewer"] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

/** Roles that can actually be handed out. Ownership is not transferable here. */
export const GRANTABLE_ROLES = ["admin", "manager", "viewer"] as const;
export type GrantableRole = (typeof GRANTABLE_ROLES)[number];

export type Capability =
  /** Add, re-role and remove members. */
  | "members:manage"
  /** Display name, description, public page and handle. */
  | "profile:manage"
  /** Read and change where money is sent. */
  | "payouts:manage"
  /** Open collabs and read the thread. */
  | "collabs:view"
  /** Counter, accept, submit drafts, confirm payments, cancel. */
  | "collabs:act"
  /** Post in the collab thread. */
  | "collabs:chat";

/**
 * Payouts stay with the owner alone. Every other permission is about doing work
 * on the account; this one changes which bank account the money lands in, so a
 * compromised or disgruntled manager must not be able to touch it.
 */
const ROLE_CAPABILITIES: Record<MemberRole, readonly Capability[]> = {
  owner: [
    "members:manage",
    "profile:manage",
    "payouts:manage",
    "collabs:view",
    "collabs:act",
    "collabs:chat",
  ],
  admin: ["members:manage", "profile:manage", "collabs:view", "collabs:act", "collabs:chat"],
  manager: ["collabs:view", "collabs:act", "collabs:chat"],
  viewer: ["collabs:view"],
};

export function capabilitiesFor(role: MemberRole): Capability[] {
  return [...ROLE_CAPABILITIES[role]];
}

export function roleCan(role: MemberRole, capability: Capability): boolean {
  return ROLE_CAPABILITIES[role].includes(capability);
}

export function isGrantableRole(value: string): value is GrantableRole {
  return (GRANTABLE_ROLES as readonly string[]).includes(value);
}

export const ROLE_LABELS: Record<MemberRole, string> = {
  owner: "Owner",
  admin: "Admin",
  manager: "Manager",
  viewer: "Viewer",
};

/**
 * Capabilities in plain language, least to most privileged, so the settings
 * matrix can be read top to bottom as "how much of the account this hands over".
 */
export const CAPABILITY_ORDER: readonly Capability[] = [
  "collabs:view",
  "collabs:chat",
  "collabs:act",
  "profile:manage",
  "members:manage",
  "payouts:manage",
];

export const CAPABILITY_LABELS: Record<Capability, string> = {
  "collabs:view": "See collabs and read threads",
  "collabs:chat": "Reply in a collab thread",
  "collabs:act": "Negotiate, accept, submit work",
  "profile:manage": "Edit the profile and public page",
  "members:manage": "Invite and remove people",
  "payouts:manage": "Change where money is sent",
};

export const ROLE_DESCRIPTIONS: Record<MemberRole, string> = {
  owner: "Full control, including where payments are sent.",
  admin: "Everything except payment details — can also manage the team.",
  manager: "Runs collaborations end to end: negotiate, accept, chat, submit work.",
  viewer: "Read-only. Can open collaborations and read the thread, nothing else.",
};
